import React, { useEffect, useState } from 'react';
import { getPaymentHistory, getPaymentSchedules, Page, PaymentHistory, PaymentSchedule } from '../api/admin';
import styles from './CommonPage.module.css';
import { ChevronLeft, ChevronRight, CreditCard } from 'lucide-react';

const PaymentsPage: React.FC = () => {
    const [data, setData] = useState<Page<PaymentHistory> | Page<PaymentSchedule> | null>(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [viewMode, setViewMode] = useState<'HISTORY' | 'SCHEDULE'>('HISTORY');
    const [statusFilter, setStatusFilter] = useState('');

    useEffect(() => {
        setPage(0);
    }, [viewMode, statusFilter]);

    useEffect(() => {
        loadData(page);
    }, [page, viewMode, statusFilter]);

    const loadData = async (pageNumber: number) => {
        setLoading(true);
        try {
            if (viewMode === 'HISTORY') {
                const result = await getPaymentHistory(pageNumber, 20, statusFilter);
                setData(result);
            } else {
                const result = await getPaymentSchedules(pageNumber, 20, statusFilter);
                setData(result);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handlePrev = () => {
        if (page > 0) setPage(page - 1);
    };

    const handleNext = () => {
        if (data && page < data.totalPages - 1) setPage(page + 1);
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h1>결제 내역</h1>
                    <p>시스템의 전체 결제 및 지급 내역을 확인합니다.</p>
                </div>
            </div>
            
            <div className={styles.controls} style={{ marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '0px', border: '1px solid #d1d5db', borderRadius: '4px', overflow: 'hidden' }}>
                    <button 
                        onClick={() => setViewMode('HISTORY')}
                        style={{
                            padding: '8px 16px',
                            background: viewMode === 'HISTORY' ? '#2563eb' : 'white',
                            color: viewMode === 'HISTORY' ? 'white' : '#374151',
                            border: 'none',
                            cursor: 'pointer'
                        }}
                    >
                        결제 내역
                    </button>
                    <button 
                        onClick={() => setViewMode('SCHEDULE')}
                        style={{
                            padding: '8px 16px',
                            background: viewMode === 'SCHEDULE' ? '#2563eb' : 'white',
                            color: viewMode === 'SCHEDULE' ? 'white' : '#374151',
                            border: 'none',
                            cursor: 'pointer',
                            borderLeft: '1px solid #d1d5db'
                        }}
                    >
                        예약 내역
                    </button>
                </div>

                <select 
                    value={statusFilter} 
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ padding: '8px', borderRadius: '4px', borderColor: '#d1d5db' }}
                >
                    <option value="ALL">전체 상태</option>
                    {viewMode === 'HISTORY' ? (
                        <>
                            <option value="PAID">결제 완료 (PAID)</option>
                            <option value="FAILED">실패 (FAILED)</option>
                            <option value="GRANTED">관리자 지급 (GRANTED)</option>
                            <option value="CANCELLED">취소 (CANCELLED)</option>
                        </>
                    ) : (
                        <>
                            <option value="PENDING">예약 중 (PENDING)</option>
                            <option value="PROCESSED">처리됨 (PROCESSED)</option>
                        </>
                    )}
                </select>
            </div>

            {loading ? (
                <div>로딩 중...</div>
            ) : (
                <>
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>사용자</th>
                                {viewMode === 'HISTORY' ? (
                                    <>
                                        <th>유형</th>
                                        <th>플랜</th>
                                        <th>금액</th>
                                        <th>상태</th>
                                        <th>결제일</th>
                                        <th>비고</th>
                                    </>
                                ) : (
                                    <>
                                        <th>플랜</th>
                                        <th>가격</th>
                                        <th>상태</th>
                                        <th>예약일</th>
                                        <th>처리일</th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {data?.content.map((item: any) => (
                                <tr key={item.id}>
                                    <td data-label="ID">{item.id}</td>
                                    <td data-label="사용자">{item.username}</td>
                                    {viewMode === 'HISTORY' ? (
                                        <>
                                            <td data-label="유형">
                                                <span style={{ 
                                                    padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600,
                                                    backgroundColor: item.paymentType === 'ADMIN' ? '#f3f4f6' : '#eff6ff',
                                                    color: item.paymentType === 'ADMIN' ? '#374151' : '#1e40af'
                                                }}>
                                                    {item.paymentType}
                                                </span>
                                            </td>
                                            <td data-label="플랜">
                                                <div>{item.planName || '-'}</div>
                                                {item.planId && <div style={{fontSize: '0.75rem', color: '#6b7280'}}>Plan ID: {item.planId}</div>}
                                                {item.relatedSubscriptionId && <div style={{fontSize: '0.75rem', color: '#6b7280'}}>Sub ID: {item.relatedSubscriptionId}</div>}
                                            </td>
                                            <td data-label="금액">{item.amount.toLocaleString()}원</td>
                                            <td data-label="상태">
                                                <span style={{ 
                                                    fontWeight: 600,
                                                    color: item.status === 'PAID' || item.status === 'GRANTED' ? '#166534' : '#991b1b'
                                                }}>
                                                    {item.status}
                                                </span>
                                            </td>
                                            <td data-label="결제일">{new Date(item.paymentDate).toLocaleString()}</td>
                                            <td data-label="비고">
                                                {item.status === 'FAILED' && (
                                                    <div>
                                                        <span style={{ color: '#ef4444' }}>{item.failureReason}</span>
                                                        <div style={{ fontSize: '0.8rem', color: '#991b1b' }}>{item.failedAt && new Date(item.failedAt).toLocaleString()}</div>
                                                    </div>
                                                )}
                                                {item.status === 'CANCELLED' && (
                                                    <div>
                                                        <span style={{ color: '#ef4444' }}>취소됨</span>
                                                        <div style={{ fontSize: '0.8rem', color: '#991b1b' }}>{item.cancelledAt && new Date(item.cancelledAt).toLocaleString()}</div>
                                                    </div>
                                                )}
                                                {item.paymentType === 'ADMIN' && <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>관리자 지급</span>}
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td data-label="플랜">{item.planName}</td>
                                            <td data-label="가격">{item.amount.toLocaleString()}원</td>
                                            <td data-label="상태">
                                                <span style={{ 
                                                    fontWeight: 600,
                                                    color: item.status === 'PROCESSED' ? '#166534' : '#d97706'
                                                }}>
                                                    {item.status}
                                                </span>
                                            </td>
                                            <td data-label="예약일">{new Date(item.scheduledAt).toLocaleString()}</td>
                                             <td data-label="처리일">
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '8px' }}>
                                                    <span>
                                                        {item.processedAt ? new Date(item.processedAt).toLocaleString() : 
                                                         item.cancelledAt ? <span style={{color: '#991b1b'}}>취소: {new Date(item.cancelledAt).toLocaleString()}</span> :
                                                         item.failedAt ? <span style={{color: '#991b1b'}}>실패: {new Date(item.failedAt).toLocaleString()}</span> :
                                                         '-'
                                                        }
                                                    </span>
                                                    {viewMode === 'SCHEDULE' && item.status === 'PENDING' && (
                                                        <button 
                                                            style={{ 
                                                                padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', 
                                                                background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={async () => {
                                                                if (window.confirm('이 결제 예약을 취소하시겠습니까?')) {
                                                                    try {
                                                                        const { cancelPaymentSchedule } = await import('../api/admin');
                                                                        await cancelPaymentSchedule(item.id);
                                                                        alert('취소되었습니다.');
                                                                        loadData(page);
                                                                    } catch (err: any) {
                                                                        alert('취소 실패: ' + (err.response?.data || err.message));
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            예약 취소
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                            {data?.content.length === 0 && (
                                <tr>
                                    <td colSpan={8} style={{ textAlign: 'center', padding: '24px' }}>결제 내역이 없습니다.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className={styles.pagination}>
                    <button onClick={handlePrev} disabled={page === 0}>
                        <ChevronLeft size={16} /> 이전
                    </button>
                    <span>
                         {data?.number !== undefined ? data.number + 1 : 1} / {data?.totalPages || 1} 페이지
                    </span>
                    <button onClick={handleNext} disabled={!data || page >= data.totalPages - 1}>
                        다음 <ChevronRight size={16} />
                    </button>
                </div>
                </>
            )}
        </div>
    );
};

export default PaymentsPage;
